"use client";

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
} from "react";
import {
  EditorContent,
  useEditor,
  useEditorState,
  type Editor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";

import { Icon, type IconName } from "@/components/ui/icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { showToast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

import { useRouter } from "next/navigation";

import { VariableMention } from "@/features/ai-prompts/components/variable";
import type { WriteWithAIRequestStepType } from "@/lib/api/model";

import { AiPromptBlock } from "./ai-prompt-extension";
import { SnippetBlock } from "./snippet-extension";
import { ListExitOnEnter } from "./list-exit-extension";
import { EditorFloatingToolbar, LinkEditorForm } from "./floating-toolbar";
import { SnippetsPanel, VariablesPanel } from "./insertion";
import type {
  InsertionMenuEnabledFeatures,
  InsertionTarget,
  SnippetAction,
} from "./insertion/types";
import {
  InlinePromptEditor,
  type InlinePromptValue,
} from "@/features/ai-prompts/components/editor";
import { InlineSnippetEditor } from "@/features/snippets/components/editor/inline-snippet-editor";

/** Discriminated state for the inline AI-prompt editor (no entity). */
type InlinePromptState =
  | { kind: "create" }
  | { kind: "edit"; initialValue: InlinePromptValue };

export type ProseMirrorDoc = Record<string, unknown>;

const EMPTY_DOC: ProseMirrorDoc = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

const DEFAULT_PLACEHOLDER =
  "Write your message… Type { to insert variables, or use the toolbar below.";

export interface StepEditorEnabledFeatures extends InsertionMenuEnabledFeatures {
  writeWithAi?: boolean;
  formatting?: boolean;
}

export interface StepEditorProps {
  jobId: string;
  sequenceId: string;
  stepType: WriteWithAIRequestStepType;
  value: ProseMirrorDoc | undefined;
  onChange: (value: ProseMirrorDoc) => void;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  enabledFeatures?: StepEditorEnabledFeatures;
  /** When provided, renders Write With AI externally (e.g. in the label row) instead of in the toolbar. */
  renderWriteWithAi?: (props: {
    currentDoc: ProseMirrorDoc;
    onApply: (doc: Record<string, unknown>, mode: "replace" | "append") => void;
    disabled: boolean;
  }) => React.ReactNode;
  /**
   * When provided, inserting a variable drops its **resolved value** as
   * literal text instead of a `{{variable}}` chip — used by the inbox
   * reply composer, where a manual reply is final copy and must never
   * ship unresolved tokens. Returns `null` when the value is unknown
   * (nothing is inserted). Omit it everywhere else to keep the default
   * chip-insertion behaviour (sequence templates, snippets, AI prompts).
   */
  resolveVariableValue?: (key: string) => string | null;
  /**
   * Optional layer rendered INSIDE the scrollable text-content area (the
   * inbox reply window uses it for the Auto Draft card, so the card sits
   * within the editor body and behind the formatting toolbar). The
   * overlay owns its own absolute positioning; the content area is its
   * positioning context.
   */
  overlay?: React.ReactNode;
}

interface ToolbarButtonProps
  extends Omit<ComponentPropsWithoutRef<"button">, "onClick"> {
  onClick: () => void;
  isActive: boolean;
  icon: IconName;
  label: string;
  disabled?: boolean;
}

/**
 * Forwards its ref and spreads remaining props so it can be used directly as a
 * Radix `PopoverTrigger asChild` child — without this, the trigger can't attach
 * its ref/click handler and the popover never opens.
 */
const ToolbarButton = forwardRef<HTMLButtonElement, ToolbarButtonProps>(
  function ToolbarButton(
    { onClick, isActive, icon, label, disabled, className, ...props },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={isActive}
        className={cn(
          "group flex size-7 cursor-pointer items-center justify-center rounded-md transition-all",
          isActive ? "bg-contrast/[0.10]" : "hover:bg-contrast/6",
          "disabled:pointer-events-none disabled:opacity-50",
          className,
        )}
        {...props}
      >
        <span
          className={cn(
            "transition-opacity",
            isActive ? "opacity-100" : "opacity-50 group-hover:opacity-70",
          )}
        >
          <Icon name={icon} size={16} className="text-heading" />
        </span>
      </button>
    );
  },
);

/**
 * Sequences step editor wrapper (Phase E / Task 42).
 *
 * Mounts the four shared extensions — StarterKit, Underline, Placeholder,
 * variable-mention, snippet-block, ai-prompt-block — and renders the
 * unified toolbar from spec §10:
 *   [B / I / U] | [Insert ▾]
 *
 * `Insert` opens the Phase E Task 40 InsertionMenu (Variables / Snippets /
 * AI Prompts tabs); the menu emits a discriminated `InsertionTarget` and
 * we dispatch the matching TipTap command.
 *
 * Persistence: fully controlled via `value` / `onChange` (matches
 * react-hook-form's `Controller` signature). The wire format is
 * `editor.getJSON()` — backend ProseMirror tree storage matches the AI
 * prompts feature pattern.
 */
export function StepEditor({
  jobId,
  sequenceId,
  stepType,
  value,
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  readOnly = false,
  className,
  enabledFeatures,
  renderWriteWithAi,
  resolveVariableValue,
  overlay,
}: StepEditorProps) {
  const features = {
    variables: enabledFeatures?.variables ?? true,
    snippets: enabledFeatures?.snippets ?? true,
    aiPrompts: enabledFeatures?.aiPrompts ?? true,
    writeWithAi: enabledFeatures?.writeWithAi ?? false,
    formatting: enabledFeatures?.formatting ?? true,
  };

  const insertionEnabled =
    features.variables || features.snippets || features.aiPrompts;

  const router = useRouter();
  // The editor's outer box — the floating toolbar mounts here so it can never
  // thrash the surrounding layout or break its own blur-to-hide.
  const editorBoxRef = useRef<HTMLDivElement>(null);
  const [variablesOpen, setVariablesOpen] = useState(false);
  const [snippetsOpen, setSnippetsOpen] = useState(false);
  const [inlinePrompt, setInlinePrompt] = useState<InlinePromptState | null>(
    null,
  );
  const [inlineSnippet, setInlineSnippet] = useState<SnippetAction | null>(null);

  const closeAllPanels = useCallback(() => {
    setVariablesOpen(false);
    setSnippetsOpen(false);
  }, []);

  // Refs so the one-time-created TipTap handleTextInput can read current state
  const triggerFlagsRef = useRef({
    variables: !readOnly && features.variables,
    snippets: !readOnly && features.snippets,
    aiPrompts: !readOnly && features.aiPrompts,
  });
  useEffect(() => {
    triggerFlagsRef.current = {
      variables: !readOnly && features.variables,
      snippets: !readOnly && features.snippets,
      aiPrompts: !readOnly && features.aiPrompts,
    };
  }, [readOnly, features.variables, features.snippets, features.aiPrompts]);

  // Stash the latest `onChange` in a ref so the TipTap `onUpdate` closure
  // (created once during `useEditor`) always calls the freshest reference
  // without re-instantiating the editor on every prop change.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);
  const lastEmittedRef = useRef<ProseMirrorDoc | undefined>(undefined);
  const lastEmittedJsonRef = useRef<string>("");

  const editor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        heading: false,
        // StarterKit v3 bundles Link + Underline — configure the link mark so
        // clicking a link opens our editor (not the URL), and only safe
        // protocols are accepted. Underline stays on by default.
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          defaultProtocol: "https",
          // Body links are https/mailto only (plain http is denied by the
          // backend sanitiser; the link editor upgrades http → https).
          protocols: ["https", "mailto"],
          HTMLAttributes: { rel: "noopener noreferrer", class: "arbi-link" },
        },
      }),
      Placeholder.configure({ placeholder, showOnlyCurrent: false }),
      VariableMention,
      SnippetBlock,
      // `setInlinePrompt` is a stable state setter, so capturing it in the
      // once-created extension config is safe.
      AiPromptBlock.configure({
        onEditNode: (attrs) =>
          setInlinePrompt({
            kind: "edit",
            initialValue: {
              instructions_doc: attrs.instructions_doc,
              length_sentences: attrs.length_sentences,
              label: attrs.label ?? null,
            },
          }),
      }),
      ListExitOnEnter,
    ],
    content: value ?? EMPTY_DOC,
    editable: !readOnly,
    onCreate: ({ editor: e }) => {
      const doc = e.getJSON() as ProseMirrorDoc;
      lastEmittedRef.current = doc;
      lastEmittedJsonRef.current = JSON.stringify(doc);
    },
    onUpdate: ({ editor: e }) => {
      const doc = e.getJSON() as ProseMirrorDoc;
      const docJson = JSON.stringify(doc);
      if (docJson === lastEmittedJsonRef.current) return;
      lastEmittedRef.current = doc;
      lastEmittedJsonRef.current = docJson;
      onChangeRef.current(doc);
    },
    editorProps: {
      handleTextInput(_view, _from, _to, text) {
        if (text === "{" && triggerFlagsRef.current.variables) {
          setSnippetsOpen(false);
          setVariablesOpen(true);
          return true;
        }
        return false;
      },
      // Plain clicks don't open links (openOnClick is off so the floating
      // toolbar can edit them); Cmd/Ctrl-click opens the link in a new tab.
      handleClick(view, pos, event) {
        if (!event.metaKey && !event.ctrlKey) return false;
        const linkType = view.state.schema.marks.link;
        if (!linkType) return false;
        const $pos = view.state.doc.resolve(pos);
        const mark =
          $pos.marks().find((m) => m.type === linkType) ??
          view.state.doc.nodeAt(pos)?.marks.find((m) => m.type === linkType);
        const href = mark?.attrs?.href;
        if (typeof href === "string" && href) {
          window.open(href, "_blank", "noopener,noreferrer");
          return true;
        }
        return false;
      },
      attributes: {
        class: cn(
          "prose prose-invert max-w-none px-3 py-2 outline-none text-sm text-contrast/90 min-h-[160px] h-full",
          "[&_.is-editor-empty:first-child::before]:text-contrast/30",
          "[&_.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]",
          "[&_.is-editor-empty:first-child::before]:float-left",
          "[&_.is-editor-empty:first-child::before]:h-0",
          "[&_.is-editor-empty:first-child::before]:pointer-events-none",
          // Tight paragraph spacing so a single Enter reads as one new line
          // (not a double-spaced gap) — matches how the live preview and the
          // sent email render paragraphs, keeping the editor WYSIWYG.
          "[&_p]:my-0 [&_p]:text-sm [&_p]:leading-normal",
          "[&_ul]:my-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-0 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:pl-0.5",
          "[&_a]:text-[#86b3ff] [&_a]:underline [&_a]:cursor-pointer",
        ),
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  // One AI prompt per email — scan the doc for an existing block so the
  // toolbar can disable the insert affordance once one exists.
  const hasAiPrompt =
    useEditorState({
      editor,
      selector: (ctx) => {
        let found = false;
        ctx.editor?.state.doc.descendants((n) => {
          if (n.type.name === "ai-prompt-block") found = true;
        });
        return found;
      },
    }) ?? false;

  // Sync external value when it diverges from what we last emitted.
  // Uses JSON comparison to avoid cursor-resetting setContent calls
  // caused by react-hook-form creating new object references.
  useEffect(() => {
    if (!editor) return;
    if (value === undefined) return;
    const valueJson = JSON.stringify(value);
    if (valueJson === lastEmittedJsonRef.current) return;
    editor.commands.setContent(value, { emitUpdate: false });
  }, [editor, value]);

  const handleInsert = useCallback(
    (target: InsertionTarget) => {
      if (!editor) return;
      switch (target.kind) {
        case "variable": {
          // Inbox replies resolve to literal text (e.g. "Mike") instead
          // of a chip; everywhere else insert the variable mention node.
          // When there's no value for this candidate, insert nothing and
          // tell the recruiter rather than dropping an empty/blank token.
          if (resolveVariableValue) {
            const resolved = resolveVariableValue(target.attrs.key);
            if (resolved) {
              editor.chain().focus().insertContent(resolved).run();
            } else {
              showToast.message("No value available for this candidate.");
            }
            break;
          }
          editor.commands.insertVariableMention(target.attrs);
          break;
        }
        case "snippet": {
          const doc = target.payload.bodyDoc;
          const content = (doc as { content?: unknown[] }).content;
          if (content && Array.isArray(content) && content.length > 0) {
            editor.chain().focus().insertContent(content).run();
          }
          break;
        }
      }
      closeAllPanels();
    },
    [editor, closeAllPanels, resolveVariableValue],
  );

  const insertSnippetContent = useCallback(
    (bodyDoc: Record<string, unknown>) => {
      if (!editor) return;
      const content = (bodyDoc as { content?: unknown[] }).content;
      if (Array.isArray(content) && content.length > 0) {
        editor.chain().focus().insertContent(content).run();
      }
    },
    [editor],
  );

  const handleAiPromptCreate = useCallback(() => {
    closeAllPanels();
    setInlinePrompt({ kind: "create" });
  }, [closeAllPanels]);

  const handleSnippetAction = useCallback(
    (action: SnippetAction) => {
      closeAllPanels();
      setInlineSnippet(action);
    },
    [closeAllPanels],
  );

  const handleAiPromptSubmit = useCallback(
    (value: InlinePromptValue) => {
      if (!editor) return;
      if (inlinePrompt?.kind === "edit") {
        editor.chain().focus().updateAiPromptBlock(value).run();
      } else {
        editor.chain().focus().insertAiPromptBlock(value).run();
      }
      setInlinePrompt(null);
    },
    [editor, inlinePrompt],
  );

  return (
    <>
      <div
        ref={editorBoxRef}
        className={cn(
          "relative flex flex-col overflow-hidden rounded-lg border border-contrast/15 bg-contrast/6 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]",
          className,
        )}
      >
        {/* `relative z-0` isolates the content (and any `overlay`) into its
            own stacking context so the toolbar below can sit ABOVE it. */}
        <div className="relative z-0 min-h-0 flex-1 overflow-auto">
          <EditorContent
            editor={editor}
            className="h-full [&_.tiptap]:h-full [&_.tiptap]:min-h-full [&>div]:h-full"
          />
          {overlay}
        </div>
        {editor && features.formatting && !readOnly ? (
          <EditorFloatingToolbar editor={editor} boundaryRef={editorBoxRef} />
        ) : null}
        <EditorToolbar
          editor={editor}
          readOnly={readOnly}
          showFormatting={features.formatting}
          showInsert={insertionEnabled}
          onInsert={handleInsert}
          insertionFeatures={{
            variables: features.variables,
            snippets: features.snippets,
            aiPrompts: features.aiPrompts,
          }}
          canInsertAiPrompt={features.aiPrompts && !hasAiPrompt}
          variablesOpen={variablesOpen}
          onVariablesOpenChange={setVariablesOpen}
          snippetsOpen={snippetsOpen}
          onSnippetsOpenChange={setSnippetsOpen}
          onAiPromptCreate={handleAiPromptCreate}
          onSnippetAction={handleSnippetAction}
          onConfigureSnippets={() => router.push("/snippets")}
        />
      </div>
      {inlinePrompt && (
        <InlinePromptEditor
          mode={inlinePrompt.kind}
          initialValue={
            inlinePrompt.kind === "edit" ? inlinePrompt.initialValue : undefined
          }
          onClose={() => setInlinePrompt(null)}
          onSubmit={handleAiPromptSubmit}
        />
      )}
      {inlineSnippet && (
        <InlineSnippetEditor
          mode={inlineSnippet.kind}
          snippetId={inlineSnippet.snippetId}
          onClose={() => setInlineSnippet(null)}
          onCreated={(bodyDoc) => {
            insertSnippetContent(bodyDoc);
            setInlineSnippet(null);
          }}
          onSaved={() => setInlineSnippet(null)}
        />
      )}
    </>
  );
}

interface EditorToolbarProps {
  editor: Editor | null;
  readOnly: boolean;
  showFormatting: boolean;
  showInsert: boolean;
  onInsert: (target: InsertionTarget) => void;
  insertionFeatures: InsertionMenuEnabledFeatures;
  /** False once an AI prompt block exists — one per email. */
  canInsertAiPrompt: boolean;
  variablesOpen: boolean;
  onVariablesOpenChange: (open: boolean) => void;
  snippetsOpen: boolean;
  onSnippetsOpenChange: (open: boolean) => void;
  onAiPromptCreate?: () => void;
  onSnippetAction?: (action: SnippetAction) => void;
  onConfigureSnippets?: () => void;
}

function EditorToolbar({
  editor,
  readOnly,
  showFormatting,
  showInsert,
  onInsert,
  insertionFeatures,
  canInsertAiPrompt,
  variablesOpen,
  onVariablesOpenChange,
  snippetsOpen,
  onSnippetsOpenChange,
  onAiPromptCreate,
  onSnippetAction,
  onConfigureSnippets,
}: EditorToolbarProps) {
  const [linkOpen, setLinkOpen] = useState(false);
  const active = useEditorState({
    editor,
    selector: (ctx) => ({
      bold: ctx.editor?.isActive("bold") ?? false,
      italic: ctx.editor?.isActive("italic") ?? false,
      underline: ctx.editor?.isActive("underline") ?? false,
      bulletList: ctx.editor?.isActive("bulletList") ?? false,
      orderedList: ctx.editor?.isActive("orderedList") ?? false,
      link: ctx.editor?.isActive("link") ?? false,
    }),
  });

  const disabled = readOnly || !editor;
  const flags = active ?? {
    bold: false,
    italic: false,
    underline: false,
    bulletList: false,
    orderedList: false,
    link: false,
  };

  // Each insert button owns an independent popover. Opening one closes the
  // others so the three panels never overlap.
  const makeOpenHandler =
    (
      setSelf: (open: boolean) => void,
      ...closers: Array<(open: boolean) => void>
    ) =>
    (next: boolean) => {
      if (disabled && next) return;
      setSelf(next);
      if (next) closers.forEach((close) => close(false));
    };

  const openVariables = makeOpenHandler(
    onVariablesOpenChange,
    onSnippetsOpenChange,
    setLinkOpen,
  );
  const openSnippets = makeOpenHandler(
    onSnippetsOpenChange,
    onVariablesOpenChange,
    setLinkOpen,
  );
  const openLink = makeOpenHandler(
    setLinkOpen,
    onVariablesOpenChange,
    onSnippetsOpenChange,
  );

  return (
    <div className="relative z-10 flex items-center justify-between border-t border-border bg-surface-header px-3 py-1.5">
      <div className="flex items-center gap-1">
        {showFormatting ? (
          <>
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBold().run()}
              isActive={flags.bold}
              icon="IconBold"
              label="Bold"
              disabled={disabled}
            />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              isActive={flags.italic}
              icon="IconItalic"
              label="Italic"
              disabled={disabled}
            />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              isActive={flags.underline}
              icon="IconUnderline"
              label="Underline"
              disabled={disabled}
            />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleOrderedList().run()}
              isActive={flags.orderedList}
              icon="IconNumberedList"
              label="Ordered list"
              disabled={disabled}
            />
            <ToolbarButton
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
              isActive={flags.bulletList}
              icon="IconBulletList"
              label="Bullet list"
              disabled={disabled}
            />
            <Popover open={linkOpen} onOpenChange={openLink}>
              <PopoverTrigger asChild>
                <ToolbarButton
                  onClick={() => {}}
                  isActive={flags.link || linkOpen}
                  icon="IconChainLink3"
                  label={flags.link ? "Edit link" : "Add link"}
                  disabled={disabled}
                />
              </PopoverTrigger>
              <PopoverContent
                align="start"
                sideOffset={6}
                className="w-auto p-0"
                onOpenAutoFocus={(event) => event.preventDefault()}
              >
                {editor ? (
                  <LinkEditorForm
                    editor={editor}
                    onClose={() => setLinkOpen(false)}
                  />
                ) : null}
              </PopoverContent>
            </Popover>
          </>
        ) : null}

        {showInsert ? (
          <>
            {showFormatting && (
              <span aria-hidden className="mx-1 h-4 w-px bg-contrast/10" />
            )}

            {insertionFeatures.snippets ? (
              <Popover open={snippetsOpen} onOpenChange={openSnippets}>
                <PopoverTrigger asChild>
                  <ToolbarButton
                    onClick={() => {}}
                    isActive={snippetsOpen}
                    icon="IconScissors2"
                    label="Snippets"
                    disabled={disabled}
                  />
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={6}
                  className="w-auto p-0"
                  onOpenAutoFocus={(event) => event.preventDefault()}
                >
                  <SnippetsPanel
                    onInsert={(payload) =>
                      onInsert({ kind: "snippet", payload })
                    }
                    onClose={() => onSnippetsOpenChange(false)}
                    onCreate={
                      onSnippetAction
                        ? () => onSnippetAction({ kind: "create" })
                        : undefined
                    }
                    onEdit={
                      onSnippetAction
                        ? (snippetId) =>
                            onSnippetAction({ kind: "edit", snippetId })
                        : undefined
                    }
                    onConfigure={onConfigureSnippets}
                  />
                </PopoverContent>
              </Popover>
            ) : null}

            {insertionFeatures.variables ? (
              <Popover open={variablesOpen} onOpenChange={openVariables}>
                <PopoverTrigger asChild>
                  <ToolbarButton
                    onClick={() => {}}
                    isActive={variablesOpen}
                    icon="IconBrackets2"
                    label="Variables"
                    disabled={disabled}
                  />
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  sideOffset={6}
                  className="w-auto p-0"
                  onOpenAutoFocus={(event) => event.preventDefault()}
                >
                  <VariablesPanel
                    onInsert={onInsert}
                    onClose={() => onVariablesOpenChange(false)}
                  />
                </PopoverContent>
              </Popover>
            ) : null}
          </>
        ) : null}
      </div>

      {insertionFeatures.aiPrompts && showInsert ? (
        <button
          type="button"
          disabled={disabled || !canInsertAiPrompt}
          onClick={() => onAiPromptCreate?.()}
          title={
            !disabled && !canInsertAiPrompt
              ? "One AI prompt per email"
              : undefined
          }
          className={cn(
            "flex cursor-pointer items-center gap-1 rounded-md px-1.5 py-1 transition-colors",
            "disabled:pointer-events-none disabled:opacity-50",
          )}
        >
          <Icon name="IconPrompt" size={16} className="text-ai" />
          <span className="bg-shine text-shine animate-bg-shine text-sm font-medium [--gradient-color:var(--ai)]">
            AI Prompts
          </span>
        </button>
      ) : null}
    </div>
  );
}
